import { getReelRoleName } from "@/helpers/UserHelpers";
import {
  SignedConventionStatsPeriod,
  SignedConventionStatsSeries,
} from "@/interfaces/SignedConventionStats";
import { DateTime } from "luxon";
import { FC, useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

type TooltipEntry = {
  dataKey: string;
  name: string;
  value: number;
  color: string;
};

interface CustomTooltipProps {
  active?: boolean;
  payload?: TooltipEntry[];
  label?: string;
}

interface Props {
  buckets: string[];
  series: SignedConventionStatsSeries[];
  period: SignedConventionStatsPeriod;
}

const PALETTE = [
  "#2c7be5",
  "#27bcfd",
  "#00d27a",
  "#f5803e",
  "#e63757",
  "#9da9bb",
  "#748194",
  "#a16eff",
  "#fac620",
  "#3874ff",
];

const formatBucket = (
  bucket: string,
  period: SignedConventionStatsPeriod,
): string => {
  const dt = DateTime.fromISO(bucket).setLocale("fr");
  if (period === "day") return dt.toFormat("dd LLL");
  if (period === "week") return `S${dt.toFormat("WW")}`;
  return dt.toFormat("LLL yyyy");
};

const displayLabel = (s: SignedConventionStatsSeries): string => {
  if (s.roles.length === 1 && s.label === s.roles[0]) {
    return getReelRoleName(s.roles);
  }
  return s.label;
};

const CustomTooltip: FC<CustomTooltipProps> = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;

  const entries = payload
    .filter((p) => typeof p.value === "number" && p.value > 0)
    .sort((a, b) => b.value - a.value);

  if (!entries.length) return null;

  const total = entries.reduce((sum, p) => sum + p.value, 0);

  return (
    <div
      className="bg-white border rounded shadow-sm px-2 py-1 fs--1"
      style={{ minWidth: 160 }}
    >
      <div className="fw-semibold mb-1">{label}</div>
      {entries.map((p) => (
        <div
          key={p.dataKey}
          className="d-flex align-items-center justify-content-between gap-2"
        >
          <span className="d-flex align-items-center gap-1">
            <span
              style={{
                display: "inline-block",
                width: 8,
                height: 8,
                background: p.color,
                borderRadius: 2,
              }}
            />
            {p.name}
          </span>
          <span className="fw-semibold">{p.value}</span>
        </div>
      ))}
      <div className="d-flex justify-content-between border-top mt-1 pt-1">
        <span className="text-muted">Total</span>
        <span className="fw-semibold">{total}</span>
      </div>
    </div>
  );
};

const SignedConventionsBarChart: FC<Props> = ({ buckets, series, period }) => {
  const { data, topSeries } = useMemo(() => {
    const top = series.slice(0, 10).map((s) => ({
      ...s,
      displayLabel: displayLabel(s),
    }));

    const rows = buckets.map((bucket) => {
      const row: Record<string, string | number> = {
        bucket: formatBucket(bucket, period),
        _bucket: bucket,
      };
      top.forEach((s) => {
        const point = s.points.find((p) => p.bucket === bucket);
        row[s.displayLabel] = point?.count ?? 0;
      });
      return row;
    });

    return { data: rows, topSeries: top };
  }, [buckets, series, period]);

  if (!series.length) {
    return (
      <div className="text-center text-muted py-5">
        Aucune convention signée sur la période sélectionnée.
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={400}>
      <BarChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 24 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#edf2f9" />
        <XAxis dataKey="bucket" tick={{ fontSize: 12 }} />
        <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
        <Tooltip
          content={<CustomTooltip />}
          cursor={{ fill: "rgba(44,123,229,0.06)" }}
          wrapperStyle={{ zIndex: 10, outline: "none" }}
          allowEscapeViewBox={{ x: false, y: true }}
        />
        <Legend
          wrapperStyle={{ fontSize: 12, paddingTop: 12 }}
          verticalAlign="bottom"
        />
        {topSeries.map((s, idx) => (
          <Bar
            key={s.key}
            dataKey={s.displayLabel}
            stackId="a"
            fill={PALETTE[idx % PALETTE.length]}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
};

export default SignedConventionsBarChart;
